<template>
  <div class="p-6">
    <h3 class="text-[18px] font-bold text-grey-light-900">Cancellation Policy</h3>
    <p class="text-[14px] text-grey-light-600 mt-1">
      If you choose to cancel this savings plan the following fees will apply.
    </p>

    <hr class="my-4" />

    <div class="space-y-2 text-[14px]">
      <div class="flex justify-between">
        <p class="text-grey-light-700">Refund amount before deductions</p>
        <p class="text-grey-light-900">{{ displayCurrency(refundBeforeDeductions) }}</p>
      </div>
      <div class="flex justify-between">
        <p class="text-grey-light-700">Cancellation fee</p>
        <p class="text-grey-light-900">{{ displayCurrency(cancellationFee) }}</p>
      </div>
      <div class="flex justify-between">
        <p class="text-grey-light-700">Reward Amount</p>
        <p class="text-grey-light-900 font-bold">{{ displayCurrency(rewardAmount) }}</p>
      </div>
    </div>

    <hr class="my-4" />

    <div class="flex justify-between text-[14px]">
      <p class="text-grey-light-700">Refund amount after deductions</p>
      <p class="text-grey-light-900 font-bold">{{ displayCurrency(refundAfterDeductions) }}</p>
    </div>

    <hr class="my-4" />

    <div>
      <p class="text-[14px] font-semibold text-grey-light-700 mb-2">Reason for cancelling</p>
      <Select :model-value="selectedReason" @update:model-value="$emit('update:selectedReason', $event)">
        <SelectTrigger>
          <SelectValue placeholder="Please select your reason" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="reason in cancellationReasons" :key="reason" :value="reason">
            {{ reason }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div
      v-if="isThresholdMet"
      class="mt-4 rounded-lg border border-success-200 bg-success-50 p-4 text-[13px] text-grey-light-700"
    >
      <div class="flex items-center gap-2 mb-1">
        <CheckCircle class="h-6 w-6 text-success-600 shrink-0" />
        <p class="font-bold text-[14px] text-success-700">Minimum threshold reached</p>
      </div>
      <p class="text-[14px] text-grey-light-700">
        You have reached your minimum payment threshold and you have earned your reward. If you cancel you will lose
        your reward and be charged a cancellation fee.
        <button type="button" class="underline font-medium text-brand-600" @click="$emit('redeem')">
          Redeem Savings
        </button>
      </p>
    </div>

    <div v-else class="mt-4 rounded-lg border border-error-200 bg-error-50 p-4 text-[13px] text-grey-light-700">
      <div class="flex items-center gap-2 mb-1">
        <AlertCircle class="h-4 w-4 text-error-600 shrink-0" />
        <p class="font-bold text-[14px] text-error-700">Minimum threshold not met!</p>
      </div>
      <p class="text-[14px] text-grey-light-700">
        Warning, if you proceed to cancel, you will be charged a cancellation fee for a cash refund. If you choose to
        redeem your voucher, you will be charged no cancellation fee.
        <button type="button" class="underline font-medium text-brand-600" @click="$emit('redeem')">
          Redeem Savings
        </button>
      </p>
    </div>

    <div class="flex items-center gap-2 mt-4">
      <Checkbox
        id="agree-policy"
        :model-value="agreedToPolicy"
        @update:model-value="$emit('update:agreedToPolicy', !!$event)"
      />
      <label for="agree-policy" class="text-[14px] text-grey-light-700 cursor-pointer">
        I agree with the <span class="underline font-medium">Cancellation Policy</span>
      </label>
    </div>

    <hr class="my-4" />

    <div class="flex gap-3">
      <Button variant="outline" class="flex-1" @click="$emit('close')">Cancel</Button>
      <Button
        class="flex-1 bg-teal-600 hover:bg-teal-700"
        :disabled="!selectedReason || !agreedToPolicy"
        @click="$emit('continue')"
      >
        Continue
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import CheckCircle from '@/components/icons/CheckCircle.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { displayCurrency } from '@/utils/format.utils';
import { AlertCircle } from 'lucide-vue-next';

defineProps<{
  refundBeforeDeductions: number;
  cancellationFee: number;
  rewardAmount: number;
  refundAfterDeductions: number;
  selectedReason: string;
  agreedToPolicy: boolean;
  isThresholdMet: boolean;
  cancellationReasons: string[];
}>();

defineEmits<{
  'update:selectedReason': [value: string];
  'update:agreedToPolicy': [value: boolean];
  continue: [];
  redeem: [];
  close: [];
}>();
</script>
